import type {
  OfferStack,
  ScriptSection,
  ParsedEmail,
  ParsedSocialPost,
  LandingPageContent,
} from '../types/results';

/*
 * As of 2026-04-16 the Make.com scenario writes every content column to
 * Supabase as a stringified JSON string. Make.com no longer produces the old
 * plain-text markdown blob, so these parsers now JSON.parse the incoming
 * string and shape it into the component-facing types. The output shapes
 * match the pre-existing public interfaces so Results/Presenter/SharedWebinar/
 * PDF consumers are unchanged.
 *
 * Every parser is defensive: it accepts an already-parsed value, a JSON
 * string, an array-wrapped object (e.g. legacy `[{...}]` landing pages), and
 * falsy input. On any parse failure it returns a safe empty value instead of
 * throwing, so the UI renders empty states instead of white-screening.
 */

// ============================================================
// SHARED HELPERS
// ============================================================

type Unknown = unknown;

function safeJsonParse<T = Unknown>(input: Unknown): T | null {
  if (input == null) return null;
  if (typeof input !== 'string') return input as T;

  const trimmed = input.trim();
  if (!trimmed) return null;

  try {
    return JSON.parse(trimmed) as T;
  } catch {
    // Tolerate accidental ```json fences that occasionally survive upstream
    const fenced = trimmed
      .replace(/^```(?:json)?\s*/i, '')
      .replace(/```\s*$/i, '')
      .trim();
    if (fenced === trimmed) return null;
    try {
      return JSON.parse(fenced) as T;
    } catch {
      return null;
    }
  }
}

function unwrapSingle<T>(value: Unknown): T | null {
  if (value == null) return null;
  if (Array.isArray(value)) {
    return (value[0] ?? null) as T | null;
  }
  return value as T;
}

function asArray<T>(value: Unknown): T[] {
  if (value == null) return [];
  return Array.isArray(value) ? (value as T[]) : [value as T];
}

function asString(value: Unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  return '';
}

function pickFirstString(source: Record<string, Unknown>, keys: string[]): string {
  for (const key of keys) {
    const val = source[key];
    if (typeof val === 'string' && val.trim()) return val;
  }
  return '';
}

function pickFirstNumber(source: Record<string, Unknown>, keys: string[]): number | null {
  for (const key of keys) {
    const val = source[key];
    if (typeof val === 'number' && Number.isFinite(val)) return val;
    if (typeof val === 'string' && val.trim()) {
      const parsed = Number(val);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

function slugify(value: string, fallback: string): string {
  const slug = value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug || fallback;
}

// ============================================================
// OFFER STACK
// ============================================================

interface RawDeliverable {
  name?: string;
  title?: string;
  deliverable?: string;
  description?: string;
  details?: string;
  blurb?: string;
  value?: string | number;
  price?: string | number;
  amount?: string | number;
}

interface RawOfferStack {
  sessionName?: string;
  name?: string;
  title?: string;
  framing?: string;
  description?: string;
  deliverables?: RawDeliverable[];
  components?: RawDeliverable[];
  totalValue?: string | number;
  priceToday?: string | number;
  investment?: string | number;
  price?: string | number;
}

function formatDollarValue(value: Unknown): string {
  if (value == null || value === '') return '';
  if (typeof value === 'number') {
    if (!Number.isFinite(value)) return '';
    return `$${value.toLocaleString('en-US')}`;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';
    return /^\$/.test(trimmed) ? trimmed : `$${trimmed}`;
  }
  return '';
}

/**
 * Parse the offer stack written by Call 1 of the Make.com scenario. The
 * column is stringified JSON with shape
 * `{ sessionName, framing, deliverables[], totalValue, priceToday }` (aliases
 * tolerated). Returns null so the OfferStackCard hides gracefully when the
 * advisor has no stored stack (pre-2026-04-16 records).
 */
export function parseOfferStack(content: string): OfferStack | null {
  const parsed = safeJsonParse<RawOfferStack | RawOfferStack[]>(content);
  if (!parsed) return null;

  const obj = unwrapSingle<RawOfferStack>(parsed);
  if (!obj || typeof obj !== 'object') return null;

  const name = pickFirstString(obj as Record<string, Unknown>, [
    'sessionName',
    'name',
    'title',
  ]);
  if (!name) return null;

  const description = pickFirstString(obj as Record<string, Unknown>, [
    'framing',
    'description',
  ]);

  const rawDeliverables: RawDeliverable[] = asArray(
    obj.deliverables ?? obj.components,
  );

  const components = rawDeliverables
    .map((d) => {
      const componentName = pickFirstString(d as Record<string, Unknown>, [
        'name',
        'title',
        'deliverable',
      ]);
      if (!componentName) return null;
      return {
        name: componentName,
        description: pickFirstString(d as Record<string, Unknown>, [
          'description',
          'details',
          'blurb',
        ]),
        value: formatDollarValue(d.value ?? d.price ?? d.amount),
      };
    })
    .filter((c): c is NonNullable<typeof c> => c !== null);

  const totalValue = formatDollarValue(obj.totalValue);
  const investment = formatDollarValue(obj.priceToday ?? obj.investment ?? obj.price) || 'Free';

  const rawText = [
    name,
    description,
    ...components.map(
      (c) => `\u2022 ${c.name}${c.value ? ` (${c.value})` : ''}${c.description ? `\n  ${c.description}` : ''}`,
    ),
    totalValue ? `Total Value: ${totalValue}` : '',
    `Investment: ${investment}`,
  ]
    .filter(Boolean)
    .join('\n');

  return {
    name,
    description,
    components,
    totalValue,
    investment,
    rawText,
  };
}

// ============================================================
// SCRIPT SECTIONS + FULL SCRIPT
// ============================================================

interface RawScriptSection {
  title?: string;
  heading?: string;
  sectionName?: string;
  durationMinutes?: number | string;
  duration?: number | string;
  minutes?: number | string;
  content?: string;
  body?: string;
  text?: string;
}

function toScriptSection(
  raw: RawScriptSection,
  index: number,
): ScriptSection | null {
  const title =
    pickFirstString(raw as Record<string, Unknown>, [
      'title',
      'heading',
      'sectionName',
    ]) || `Section ${index + 1}`;

  const content = pickFirstString(raw as Record<string, Unknown>, [
    'content',
    'body',
    'text',
  ]);
  if (!content) return null;

  const minutes = pickFirstNumber(raw as Record<string, Unknown>, [
    'durationMinutes',
    'duration',
    'minutes',
  ]);

  return {
    id: slugify(title, `section-${index + 1}`),
    title,
    content,
    timeEstimate: minutes != null ? `${minutes} min` : undefined,
  };
}

export function parseScriptSections(content: string): ScriptSection[] {
  const parsed = safeJsonParse<RawScriptSection | RawScriptSection[]>(content);
  if (!parsed) return [];

  const arr = asArray<RawScriptSection>(parsed);
  return arr
    .map((raw, idx) => toScriptSection(raw, idx))
    .filter((s): s is ScriptSection => s !== null);
}

/**
 * Concatenate all script sections into a single markdown string suitable for
 * the Full Script view, PDF, Presenter, and the public /script/:token page.
 */
export function extractFullScript(content: string): string {
  const sections = parseScriptSections(content);
  if (sections.length === 0) {
    // If the incoming string isn't JSON (legacy rows), fall back to the raw text
    const raw = typeof content === 'string' ? content.trim() : '';
    return raw;
  }

  return sections
    .map((s) => {
      const header = s.timeEstimate
        ? `## ${s.title} (${s.timeEstimate})`
        : `## ${s.title}`;
      return `${header}\n\n${s.content.trim()}`;
    })
    .join('\n\n---\n\n');
}

// ============================================================
// WEBINAR METADATA (hero block)
// ============================================================

export interface WebinarMetadata {
  title: string;
  subtitle: string;
  bullets: string[];
}

interface WebinarMetadataInput {
  title?: string | null;
  subtitle?: string | null;
  /** Landing page column (stringified JSON) — used to source bullet hooks */
  landingPage?: string | null;
}

/**
 * Build the WebinarHero payload. Title and subtitle live in top-level Supabase
 * columns as plain strings, and the bullets come from the landing page
 * `keyHooks` array. Accepts a combined options object so the caller threads
 * both Supabase columns in.
 */
export function parseWebinarMetadata(
  input: WebinarMetadataInput | string | null | undefined,
): WebinarMetadata | null {
  if (input == null) return null;

  // Legacy callers passed the raw webinar_script text; we can't recover
  // title/subtitle from it anymore. Return null so the hero simply hides.
  if (typeof input === 'string') return null;

  const title = (input.title ?? '').trim();
  const subtitle = (input.subtitle ?? '').trim();

  const landing = parseLandingPage(input.landingPage ?? '');
  const bullets = landing?.hooks ?? [];

  if (!title && !subtitle && bullets.length === 0) return null;

  return { title, subtitle, bullets };
}

// ============================================================
// EMAILS (marketing + follow-up share the same shape)
// ============================================================

interface RawEmail {
  emailNumber?: number | string;
  number?: number | string;
  sendWhen?: string;
  sendDay?: string;
  when?: string;
  phase?: string;
  angle?: string;
  subjectLine?: string;
  subject?: string;
  body?: string;
  content?: string;
  text?: string;
}

function toParsedEmail(raw: RawEmail, index: number): ParsedEmail | null {
  const number =
    pickFirstNumber(raw as Record<string, Unknown>, ['emailNumber', 'number']) ??
    index + 1;
  const subject = pickFirstString(raw as Record<string, Unknown>, [
    'subjectLine',
    'subject',
  ]);
  const body = pickFirstString(raw as Record<string, Unknown>, [
    'body',
    'content',
    'text',
  ]);
  if (!subject && !body) return null;

  const sendDay = pickFirstString(raw as Record<string, Unknown>, [
    'sendWhen',
    'sendDay',
    'when',
  ]);

  const phase = pickFirstString(raw as Record<string, Unknown>, ['phase']);
  const angle = pickFirstString(raw as Record<string, Unknown>, ['angle']);

  const rawText = [
    `Email ${number}${sendDay ? ` — ${sendDay}` : ''}`,
    subject ? `Subject: ${subject}` : '',
    body,
  ]
    .filter(Boolean)
    .join('\n\n');

  return { number, sendDay, subject, body, rawText, phase, angle };
}

export function parseEmails(content: string): ParsedEmail[] {
  const parsed = safeJsonParse<RawEmail | RawEmail[]>(content);
  if (!parsed) return [];

  const arr = asArray<RawEmail>(parsed);
  return arr
    .map((raw, idx) => toParsedEmail(raw, idx))
    .filter((e): e is ParsedEmail => e !== null);
}

// ============================================================
// SOCIAL POSTS
// ============================================================

interface RawSocialPost {
  postNumber?: number | string;
  number?: number | string;
  sendWhen?: string;
  dayLabel?: string;
  when?: string;
  phase?: string;
  angle?: string;
  body?: string;
  content?: string;
  text?: string;
}

function toParsedSocialPost(
  raw: RawSocialPost,
  index: number,
): ParsedSocialPost | null {
  const content = pickFirstString(raw as Record<string, Unknown>, [
    'body',
    'content',
    'text',
  ]);
  if (!content) return null;

  const number =
    pickFirstNumber(raw as Record<string, Unknown>, ['postNumber', 'number']) ??
    index + 1;
  const dayLabel = pickFirstString(raw as Record<string, Unknown>, [
    'sendWhen',
    'dayLabel',
    'when',
  ]);

  const phase = pickFirstString(raw as Record<string, Unknown>, ['phase']);
  const angle = pickFirstString(raw as Record<string, Unknown>, ['angle']);

  return {
    number,
    dayLabel,
    content,
    characterCount: content.length,
    phase,
    angle,
  };
}

export function parseSocialPosts(content: string): ParsedSocialPost[] {
  const parsed = safeJsonParse<RawSocialPost | RawSocialPost[]>(content);
  if (!parsed) return [];

  const arr = asArray<RawSocialPost>(parsed);
  return arr
    .map((raw, idx) => toParsedSocialPost(raw, idx))
    .filter((p): p is ParsedSocialPost => p !== null);
}

// ============================================================
// LANDING PAGE
// ============================================================

interface RawSecret {
  title?: string;
  name?: string;
  heading?: string;
  teaser?: string;
  paragraph?: string;
  body?: string;
  content?: string;
}

interface RawLandingPage {
  headline?: string;
  title?: string;
  subHeadline?: string;
  subtitle?: string;
  urgencyDescription?: string;
  description?: string;
  keyHooks?: string[];
  hooks?: string[];
  secrets?: RawSecret[];
}

function toSecret(raw: RawSecret): { title: string; paragraph: string } | null {
  const title = pickFirstString(raw as Record<string, Unknown>, [
    'title',
    'name',
    'heading',
  ]);
  const paragraph = pickFirstString(raw as Record<string, Unknown>, [
    'teaser',
    'paragraph',
    'body',
    'content',
  ]);
  if (!title && !paragraph) return null;
  return { title, paragraph };
}

export function parseLandingPage(content: string): LandingPageContent | null {
  const parsed = safeJsonParse<RawLandingPage | RawLandingPage[]>(content);
  if (!parsed) return null;

  const obj = unwrapSingle<RawLandingPage>(parsed);
  if (!obj || typeof obj !== 'object') return null;

  const title = pickFirstString(obj as Record<string, Unknown>, [
    'headline',
    'title',
  ]);
  const subtitle = pickFirstString(obj as Record<string, Unknown>, [
    'subHeadline',
    'subtitle',
  ]);
  const description = pickFirstString(obj as Record<string, Unknown>, [
    'urgencyDescription',
    'description',
  ]);

  const hooksSource = obj.keyHooks ?? obj.hooks ?? [];
  const hooks = Array.isArray(hooksSource)
    ? hooksSource.map(asString).filter((h) => h.length > 0)
    : [];

  const secretsSource = Array.isArray(obj.secrets) ? obj.secrets : [];
  const secrets = secretsSource
    .map(toSecret)
    .filter((s): s is { title: string; paragraph: string } => s !== null);

  if (!title && !subtitle && !description && hooks.length === 0 && secrets.length === 0) {
    return null;
  }

  return { title, subtitle, description, hooks, secrets };
}
